import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, ListOrdered, ShieldCheck, Network, RotateCcw, Hexagon, FlaskConical,
  Handshake, Palette, ArrowLeftRight, Workflow, ClipboardCheck, Activity, TrendingUp, MonitorPlay, X, Menu, Compass,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";
import { ROLE_VIEWS } from "@/lib/views";
import { TOUR_STEPS } from "@/lib/tour";
import { TourOverlay } from "@/components/Tour";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/live", label: "Live Operations", icon: Activity, testId: "nav-live" },
  { to: "/impact", label: "Operational Impact", icon: TrendingUp, testId: "nav-impact" },
  { to: "/queue", label: "Client Queue", icon: ListOrdered, testId: "nav-queue" },
  { to: "/governance", label: "Governance Centre", icon: ShieldCheck, testId: "nav-governance" },
  { to: "/integrations", label: "Integrations", icon: Network, testId: "nav-integrations" },
];

const PARTNERSHIP_NAV = [
  { to: "/partnership", label: "Deployment Modes", icon: Handshake, end: true, testId: "nav-partnership" },
  { to: "/partnership/whitelabel", label: "White-Label Preview", icon: Palette, testId: "nav-whitelabel" },
  { to: "/partnership/comparison", label: "Comparison", icon: ArrowLeftRight, testId: "nav-comparison" },
  { to: "/partnership/architecture", label: "Integration Architecture", icon: Workflow, testId: "nav-architecture" },
  { to: "/partnership/evaluation", label: "Partner Evaluation", icon: ClipboardCheck, testId: "nav-evaluation" },
];

const navClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200 ${
    isActive
      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border border-transparent"
  }`;

const NavItem = ({ item }) => (
  <NavLink to={item.to} end={item.end} data-testid={item.testId} className={navClass}>
    <item.icon className="w-4 h-4" strokeWidth={1.5} />
    {item.label}
  </NavLink>
);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [present, setPresent] = useState(() => localStorage.getItem("onet_present") === "1");
  const [menuOpen, setMenuOpen] = useState(false);
  const [tourStep, setTourStep] = useState(-1);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (localStorage.getItem("onet_tour_pending") === "1") {
      localStorage.removeItem("onet_tour_pending");
      setTourStep(0);
    }
  }, []);

  useEffect(() => {
    if (tourStep < 0) return;
    const s = TOUR_STEPS[tourStep];
    if (s.caseId) localStorage.setItem("onet_tour_last_case", s.caseId);
    const target = s.route + (s.caseId ? `?case=${s.caseId}` : "") + (s.replay ? `${s.caseId ? "&" : "?"}replay=${tourStep}` : "");
    if (location.pathname + location.search !== target) navigate(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourStep]);

  const endTour = (route) => {
    localStorage.setItem("onet_tour_done", "1");
    setTourStep(-1);
    if (route) navigate(route);
  };

  useEffect(() => {
    const h = () => setPresent(localStorage.getItem("onet_present") === "1");
    window.addEventListener("onet-present", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("onet-present", h); window.removeEventListener("storage", h); };
  }, []);

  const exitPresent = () => {
    localStorage.removeItem("onet_present");
    setPresent(false);
  };

  const resetDemo = async () => {
    try {
      await api.post("/demo/reset");
      toast.success("Demo data reset", { description: "All synthetic clients, workflows and audit logs restored." });
      navigate("/");
      window.location.reload();
    } catch (e) {
      toast.error(apiError(e, "Reset failed"));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="app-layout">
      {menuOpen && !present && (
        <div
          data-testid="drawer-backdrop"
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <aside className={`w-64 shrink-0 fixed inset-y-0 left-0 bg-zinc-950 border-r border-zinc-800 flex flex-col z-40 transform transition-transform duration-200 ${present ? "hidden" : ""} ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar-nav">
        <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Hexagon className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display font-bold text-zinc-100 tracking-tight leading-none">O.N.E.Tech</div>
            <div className="text-[10px] font-mono text-emerald-500 tracking-widest mt-1">OPERATIONAL INTELLIGENCE</div>
          </div>
          <button
            data-testid="mobile-menu-close"
            onClick={() => setMenuOpen(false)}
            title="Close menu"
            className="ml-auto lg:hidden text-zinc-500 hover:text-zinc-200 transition-colors duration-200 p-1"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
          <div className="pt-4 pb-1 px-3 text-[9px] font-mono tracking-widest text-zinc-600 uppercase">
            Deployment &amp; Partnership
          </div>
          {PARTNERSHIP_NAV.map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        </nav>
        <div className="px-3 pb-4 space-y-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="reset-demo-button"
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm text-xs font-semibold bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 hover:border-emerald-500/40 transition-colors duration-200"
              >
                <RotateCcw className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} />
                Reset Demo Data
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-900 border border-zinc-700" data-testid="reset-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-zinc-100 font-display">Reset demonstration data?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-400 text-sm">
                  All synthetic client scenarios, workflow states, timelines, dashboard metrics and audit log entries will be restored to their original seeded state. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel-button" className="bg-zinc-800 text-zinc-200 border-zinc-700 hover:bg-zinc-700 hover:text-zinc-100 rounded-sm">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="reset-confirm-button"
                  onClick={resetDemo}
                  className="bg-emerald-500 text-zinc-950 hover:bg-emerald-600 rounded-sm font-semibold"
                >
                  Reset Demo Data
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </aside>
      <div className={`flex-1 min-w-0 ${present ? "" : "lg:ml-64"} flex flex-col min-h-screen`}>
        <div
          data-testid="synthetic-data-banner"
          className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-emerald-500/30 px-4 sm:px-6 py-2 flex flex-wrap items-center gap-x-2 gap-y-1.5"
        >
          {!present && (
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              title="Open menu"
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/40 transition-colors duration-200"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
          )}
          <FlaskConical className="w-3.5 h-3.5 text-emerald-400 shrink-0" strokeWidth={1.5} />
          <span className="text-[11px] font-mono tracking-widest text-emerald-400 uppercase min-w-0">
            Synthetic Demonstration Environment — simulated CRM, KYC, payments, MT5 &amp; messaging. No live clients, systems or funds.
          </span>
          <span className="lg:ml-auto flex items-center gap-1.5 max-w-full overflow-x-auto whitespace-nowrap" data-testid="view-selector">
            {ROLE_VIEWS.map((v) => (
              <Link
                key={v.key}
                to={v.to}
                data-testid={`view-${v.key}`}
                className={`px-2 py-1 rounded-sm text-[10px] font-semibold border transition-colors duration-150 ${
                  location.pathname === v.to
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
                    : "text-zinc-400 border-transparent hover:text-emerald-400 hover:border-zinc-700"
                }`}
              >
                {v.label}
              </Link>
            ))}
            <button
              data-testid="guided-tour-button"
              onClick={() => setTourStep(0)}
              className="ml-1 flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold text-zinc-400 border border-zinc-700 hover:text-emerald-400 hover:border-emerald-500/40 transition-colors duration-150"
            >
              <Compass className="w-3 h-3" /> Guided Tour
            </button>
            {present ? (
              <button
                data-testid="exit-present-button"
                onClick={exitPresent}
                className="ml-1 flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold bg-zinc-800 text-zinc-100 border border-zinc-700 hover:bg-zinc-700 transition-colors duration-150"
              >
                <X className="w-3 h-3" /> Exit Presentation
              </button>
            ) : (
              <button
                data-testid="present-mode-button"
                onClick={() => navigate("/presentation")}
                className="ml-1 flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors duration-150"
              >
                <MonitorPlay className="w-3 h-3" /> Presentation Mode
              </button>
            )}
          </span>
        </div>
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
        {tourStep >= 0 && (
          <TourOverlay
            stepIdx={tourStep}
            onNext={() => setTourStep((i) => Math.min(i + 1, TOUR_STEPS.length - 1))}
            onBack={() => setTourStep((i) => Math.max(i - 1, 0))}
            onSkip={() => endTour()}
            onFinish={endTour}
          />
        )}
      </div>
    </div>
  );
}
